import { useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import {
  FiArrowLeft,
  FiChevronRight,
  FiCreditCard,
  FiMapPin,
  FiUser,
} from "react-icons/fi";
import {
  bookVenueSlot,
  updatePayment,
  getBookingDetails,
} from "../../services/adminApiService/venueManagerApi/venueManagerApi.js";
import "./styles.css";

const EMPTY_VALUE = "---";
const DEFAULT_AMOUNT = 1200;

const toTitleCase = (value) =>
  value
    ? String(value)
        .replace(/[-_]/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
    : "";

const displayValue = (value) => {
  if (value === 0) return "0";
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;

  return value;
};

const formatDate = (value) => {
  if (!value) return EMPTY_VALUE;

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return displayValue(value);

  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

export default function PaymentSummary() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const location = useLocation();
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [error, setError] = useState("");
  const [bookingId, setBookingId] = useState("");
  const [showModalFail, setShowModalFail] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [paymentProcessingMessage, setPaymentProcessingMessage] = useState("");
  const paymentAttemptRef = useRef(0);

  useEffect(() => {
    if (!location.state || !location.state.booking) {
      navigate("/book-for-others");
    }
  }, [location, navigate]);

  const getStoredBooking = () => {
    try {
      return JSON.parse(localStorage.getItem("bookingFormData")) || {};
    } catch {
      return {};
    }
  };

  const getLoginUser = () => {
    try {
      return JSON.parse(localStorage.getItem("login_Details")) || {};
    } catch {
      return {};
    }
  };

  const bookingState = state?.booking ?? {};
  const userState = state?.user ?? {};

  const amount = Number(state?.amount ?? DEFAULT_AMOUNT);
  const isPayOnlineDisabled = amount === 0;

  const storedBooking = getStoredBooking();

  const loginUser = getLoginUser()?.user || {}; // ✅ FIXED

  const booking = {
    ...storedBooking,
    ...bookingState,
  };

  const user = {
    ...loginUser,
    ...userState,
  };

  const getBookingUserId = (fallbackUserId) =>
    fallbackUserId ||
    userState?.id ||
    userState?.user_id ||
    booking?.user_id ||
    booking?.userId ||
    "";

  const summary = useMemo(() => {
    const amount = Number(state?.amount ?? DEFAULT_AMOUNT);

    return {
      amount,

      venueName: displayValue(state?.venueName || booking.clusterName),
      address: displayValue(state?.venueAddress || booking.venueName),

      fields: [
        {
          label: "Name",
          value: displayValue(
            userState.username ||
              booking?.fullName ||
              `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
              booking.first_name ||
              booking.fullName,
          ),
        },
        {
          label: "Contact Number",
          value: displayValue(
            userState.mobile ||
              booking?.mobileNumber ||
              user.phone ||
              user.mobile ||
              booking.mobileNumber,
          ),
        },
        // {
        //   label: "Gender",
        //   value: displayValue(toTitleCase(user.gender)),
        // },
        // {
        //   label: "State",
        //   value: displayValue(user.state_id || booking.state),
        // },
        // {
        //   label: "District",
        //   value: displayValue(user.district_id || booking.district),
        // },
        {
          label: "Date",
          value: formatDate(booking.bookingDate),
        },
        {
          label: "Sport",
          value: displayValue(state?.sportName || booking.sport),
        },
        // {
        //   label: "Time Slot",
        //   value: displayValue(booking.timeSlot),
        // },
        {
          label: "Participants",
          value: displayValue(booking.participants),
        },
        {
          label: "Field of Play",
          value: displayValue(state?.zoneName || booking.zone),
        },
        {
          label: "Amount",
          value: formatCurrency(amount),
        },
      ],
    };
  }, [state]);
  const gst = 0;
  const total = summary.amount + gst;

  useEffect(() => {
    if (isPayOnlineDisabled && paymentMethod !== "cash") {
      setPaymentMethod("cash");
    }
  }, [isPayOnlineDisabled, paymentMethod]);
  const handleUpdatePayment = async ({
    payment_id = null,
    order_id,
    err = null,
    status = "",
    user_id,
  }) => {
    try {
      const payload = {
        payment_id,
        order_id,
        user_id,
      };

      // Optional fields
      if (err) payload.err = err;
      if (status) payload.status = status;

      const response = await updatePayment(payload);

      return {
        success: true,
        data: response,
        errorMessage: "",
      };
    } catch (error) {
      console.error("updatePayment Error:", error);

      return {
        success: false,
        data: null,
        errorMessage:
          error?.response?.data?.message ||
          error?.message ||
          "Failed to update payment status.",
      };
    }
  };

  const handlePaymentSubmit = async () => {
    if (isPaymentProcessing) return;

    if (!paymentMethod) {
      setError("Please select a payment method");
      return;
    }

    setError("");

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to buy the Ticket?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    });

    if (!result.isConfirmed) return;

    const paymentAttemptId = paymentAttemptRef.current + 1;
    paymentAttemptRef.current = paymentAttemptId;
    const isCurrentPaymentAttempt = () =>
      paymentAttemptRef.current === paymentAttemptId;
    const stopPaymentLoader = () => {
      if (!isCurrentPaymentAttempt()) return;
      setIsPaymentProcessing(false);
      setPaymentProcessingMessage("");
    };

    setIsPaymentProcessing(true);
    setPaymentProcessingMessage("Creating booking...");

    const bookingData = {
      sport_id: booking?.sport_id,
      venue_id: booking?.venue,
      slot_id: booking?.timeSlot,
      payment_plateform_type: paymentMethod,
      fullname:
        userState.username ||
        booking?.fullName ||
        `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
        booking.fullName ||
        "",
      mobile:
        userState?.mobile ||
        booking?.mobileNumber ||
        user.phone ||
        user.mobile ||
        booking.mobileNumber ||
        "",
      gender: booking.gender || "",
      state: booking.state || "",
      district: booking.district === undefined ? "" : booking.district,
      user_id: getBookingUserId(),
      participants: booking.participants,
    };

    try {
      // ✅ STEP 1: Create Booking
      const confirmation = await bookVenueSlot(bookingData);
      // 🔥 Extract message safely
      // console.log(confirmation, "confirmation");
      const errorMessage =
        confirmation?.message ||
        confirmation?.error ||
        confirmation?.data?.message ||
        "Something went wrong during booking.";

      if (!confirmation?.status) {
        stopPaymentLoader();

        await Swal.fire({
          icon: "error",
          title: "Booking Failed",
          text: errorMessage,
        });
        return;
      }

      const bookingId = confirmation?.data?.data?.id;
      const userId = getBookingUserId(confirmation?.data?.data?.user_id);

      setPaymentProcessingMessage("Creating payment order...");

      // ✅ STEP 2: CASH FLOW
      if (paymentMethod === "cash") {
        const token = localStorage.getItem("access_token_admin");

        const { data: order } = await axios.post(
          `${process.env.REACT_APP_BOOKING_URL}/api/auth/payment/orderNew`,
          {
            amount: state?.amount * 100,
            currency: "INR",
            booking_id: bookingId,
            payment_plateform_type: paymentMethod,
            user_id: userId,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
          },
        );

        stopPaymentLoader();

        await Swal.fire({
          icon: "success",
          title: "Ticket Booked!",
          text: confirmation.message || "Your booking was successful.",
        });

        navigate("/manager-ticket-details", {
          state: { id: bookingId },
        });
        return;
      }

      // ✅ STEP 3: ONLINE PAYMENT
      const token = localStorage.getItem("access_token_admin");

      const { data: order } = await axios.post(
        `${process.env.REACT_APP_BOOKING_URL}/api/auth/payment/orderNew`,
        {
          amount: state?.amount * 100,
          currency: "INR",
          booking_id: bookingId,
          payment_plateform_type: paymentMethod,
          user_id: userId,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
        },
      );

      if (!order?.order_id) {
        throw new Error("Order ID not received");
      }

      const orderId = order.order_id;
      localStorage.setItem("order_id", orderId);
      setPaymentProcessingMessage("Opening payment gateway...");

      let isCheckoutSettled = false;
      let failedPaymentError = null;

      const settleCheckout = () => {
        if (isCheckoutSettled) return false;
        isCheckoutSettled = true;
        return true;
      };

      // ✅ STEP 4: Razorpay Options
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: "Sports Booking",
        description: "Slot Booking",
        order_id: orderId,

        handler: async function (response) {
          if (!settleCheckout()) return;

          try {
            setPaymentProcessingMessage("Confirming payment...");

            // ✅ SUCCESS PAYMENT API CALL
            const obj = {
              payment_id: response?.razorpay_payment_id,
              order_id: orderId,
              user_id: userId,
            };

            await handleUpdatePayment({
              payment_id: response?.razorpay_payment_id,
              order_id: orderId,
              user_id: userId,
            });

            // ✅ ONLY SHOW IF ERROR EXISTS
            // if (!updateResponse.success) {
            //   await Swal.fire({
            //     icon: "warning",
            //     title: "Payment Status Update Failed",
            //     text: updateResponse.errorMessage,
            //   });
            // }

            if (!isCurrentPaymentAttempt()) return;

            stopPaymentLoader();

            await Swal.fire({
              title: "Success!",
              text: "Payment Successful",
              icon: "success",
            });

            if (!isCurrentPaymentAttempt()) return;

            navigate("/manager-ticket-details", {
              state: { id: bookingId },
            });
          } catch (err) {
            console.error("Update payment error:", err);
            stopPaymentLoader();
          }
        },

        prefill: {
          name: user?.name,
          email: user?.email,
        },

        modal: {
          ondismiss: async function () {
            if (!settleCheckout() || !isCurrentPaymentAttempt()) return;

            try {
              setPaymentProcessingMessage("Updating payment status...");

              const obj = failedPaymentError
                ? {
                    payment_id:
                      failedPaymentError?.metadata?.payment_id || null,
                    order_id: orderId,
                    err: failedPaymentError,
                    user_id: userId,
                  }
                : {
                    payment_id: null,
                    order_id: orderId,
                    status: "dismissed",
                    user_id: userId,
                  };
              if (paymentMethod !== "cash") {
                await updatePayment(obj);
              }
            } catch (err) {
              console.warn("Dismiss update failed (ignored):", err);
            }

            if (!isCurrentPaymentAttempt()) return;

            stopPaymentLoader();

            if (failedPaymentError) {
              await Swal.fire({
                icon: "error",
                title: "Payment Failed",
                html: `
                <h5 style="font-weight:bold;">Payment Failed</h5>
                
              `,
              });
            } else {
              // ✅ Show proper message (NOT error)
              await Swal.fire({
                icon: "warning",
                title: "Payment Failed",
                text: "Payment Failed.",
              });
            }

            if (!isCurrentPaymentAttempt()) return;

            navigate("/manager-ticket-details", {
              state: { id: bookingId },
            });
          },
        },
      };

      if (!window.Razorpay) {
        throw new Error("Razorpay SDK not loaded");
      }

      const rzp = new window.Razorpay(options);

      // Razorpay can still allow retry after this event, so keep it pending
      // until the checkout closes without a later success.
      rzp.on("payment.failed", function (response) {
        if (isCheckoutSettled) return;
        failedPaymentError = response?.error || null;
      });

      // 🚀 OPEN RAZORPAY
      setPaymentProcessingMessage("Waiting for payment response...");
      rzp.open();
    } catch (err) {
      console.error("Payment error:", err);
      stopPaymentLoader();
      if (err?.response?.data?.message) {
        await Swal.fire({
          icon: "error",
          title: "Booking Failed",
          text: err.response.data.message,
        });
        navigate("/book-for-others");
        return;
      }

      await Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: "Unable to start payment. Try again.",
      });
    }
  };

  return (
    <div className="payment-page">
      {isPaymentProcessing && (
        <div
          className="payment-loader-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="payment-loader-card">
            <div className="payment-loader-spinner" />
            <strong>
              {paymentProcessingMessage || "Processing payment..."}
            </strong>
            <span>Please wait while we get the payment response.</span>
          </div>
        </div>
      )}

      {/* <nav className="payment-breadcrumb" aria-label="Breadcrumb">
        <span>Home</span>
        <FiChevronRight />
        <span>Dashboard</span>
        <FiChevronRight />
        <span>Book For Others</span>
        <FiChevronRight />
        <strong>Payment Summary</strong>
      </nav> */}

      <h1 className="payment-page-title">Payment Summary</h1>

      <section className="payment-card">
        <button
          type="button"
          className="payment-back"
          onClick={() => navigate(-1)}
        >
          <FiArrowLeft />
          Back
        </button>

        <div className="payment-layout">
          <div className="payment-details">
            <p className="payment-eyebrow">Payment Details</p>
            <h2>{summary.venueName}</h2>
            <p
              className="payment-location"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "6px",
              }}
            >
              <FiMapPin  style={{ marginTop: "3px", flexShrink: 0 }} />

              <span>{summary.address}</span>
            </p>

            <div className="payment-divider" />

            <h3>Booking Details</h3>
            <dl className="payment-detail-list">
              {summary.fields.map((item) => (
                <div key={item.label} className="payment-detail-row">
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <aside className="payment-panel">
            <h3>Payment Method</h3>

            <div className="payment-method-box">
              <label
                className={`payment-method-option ${isPayOnlineDisabled ? "disabled" : ""}`}
              >
                <input
                  type="radio"
                  name="paymentMethod"
                  value="pay_online"
                  checked={paymentMethod === "pay_online"}
                  disabled={isPaymentProcessing || isPayOnlineDisabled}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>Pay Online</span>
                <FiCreditCard />
              </label>

              <label className="payment-method-option">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="cash"
                  checked={paymentMethod === "cash"}
                  disabled={isPaymentProcessing}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                />
                <span>Cash</span>
                <FiUser />
              </label>
            </div>

            <h3 className="payment-summary-title">Payment Summary</h3>

            <div className="payment-total-row">
              <span>Sub Total</span>
              <strong>{formatCurrency(summary.amount)}</strong>
            </div>
            <div className="payment-total-row">
              <span>GST:</span>
              <strong>{formatCurrency(gst)}</strong>
            </div>
            <div className="payment-total-row payment-grand-total">
              <span>Total</span>
              <strong>{formatCurrency(total)}</strong>
            </div>
          </aside>
        </div>

        <div className="payment-actions">
          <button
            type="button"
            className="payment-btn payment-btn-secondary"
            disabled={isPaymentProcessing}
            onClick={() => navigate("/book-for-others")}
          >
            Cancel
          </button>
          <button
            type="button"
            className="payment-btn payment-btn-primary"
            disabled={isPaymentProcessing}
            onClick={handlePaymentSubmit}
          >
            {isPaymentProcessing ? "Processing..." : "Continue"}
          </button>
        </div>
      </section>
    </div>
  );
}
